/**
 * 能量图（M4-01 / DESIGN.md §2.11、§6.5）。
 *
 * 能量 = 梯度幅值（水平/垂直中心差分的绝对值之和，作用在亮度上）——
 * Seam Carving 的经典选择，简单且对高频细节（边缘、纹理）敏感，
 * 缝会绕开这些区域，正是我们想要的"保留视觉重要内容"效果。
 */

/** RGBA8 → 亮度（ITU-R BT.601，与本项目其余灰度计算一致）。 */
function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/**
 * 计算能量图（Float32Array，行主序，宽 width、高 height）。
 * 边界像素用相邻像素代替越界采样（clamp-to-edge），与本项目卷积类算法
 * 的既有约定一致（engine/effects/render.ts 的模糊也是这个边界策略）。
 */
export function computeEnergy(pixels: Uint8Array, width: number, height: number): Float32Array {
  const lum = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      lum[y * width + x] = luminance(pixels[i]!, pixels[i + 1]!, pixels[i + 2]!);
    }
  }

  const energy = new Float32Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const xm = x > 0 ? x - 1 : x;
      const xp = x < width - 1 ? x + 1 : x;
      const ym = y > 0 ? y - 1 : y;
      const yp = y < height - 1 ? y + 1 : y;
      const dx = lum[y * width + xp]! - lum[y * width + xm]!;
      const dy = lum[yp * width + x]! - lum[ym * width + x]!;
      energy[y * width + x] = Math.abs(dx) + Math.abs(dy);
    }
  }
  return energy;
}

/**
 * 用蒙版调制能量：保护区（mask 高）能量拉到极大（缝绝不穿过），
 * 移除区（mask 高）能量拉到极小（缝优先穿过，加速删除该区域内容）。
 * 两者不会同时传——由调用方决定这块图是保护还是移除。
 */
export function applyEnergyMask(
  energy: Float32Array,
  mask: Uint8Array,
  mode: 'protect' | 'remove',
): void {
  const bias = mode === 'protect' ? 1e6 : -1e6;
  for (let i = 0; i < energy.length; i++) {
    const factor = mask[i]! / 255;
    if (factor > 0) energy[i] = energy[i]! + bias * factor;
  }
}
